import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Pencil, Trash2, Search, Star, Shield, Phone, Mail, Globe, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const TRADES = ['excavation','steel','plumbing','electrical','gunite','tile','decking','plaster','equipment','landscaping','fencing','concrete','gas','engineering','permits','general','other'];
const TRADE_COLORS = { excavation:'bg-amber-100 text-amber-700', steel:'bg-slate-200 text-slate-700', plumbing:'bg-sky-100 text-sky-700', electrical:'bg-yellow-100 text-yellow-700', gunite:'bg-stone-200 text-stone-700', tile:'bg-violet-100 text-violet-700', decking:'bg-emerald-100 text-emerald-700', equipment:'bg-orange-100 text-orange-700' };
const EMPTY = { name:'', trade:'general', contact_name:'', phone:'', email:'', address:'', website:'', insurance_on_file:false, insurance_expiry:'', license_number:'', rating:0, notes:'', is_active:true };

export default function Vendors() {
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [search, setSearch] = useState('');
  const [tradeFilter, setTradeFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editVendor, setEditVendor] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    const params = {};
    if (tradeFilter !== 'all') params.trade = tradeFilter;
    if (search) params.search = search;
    const r = await api.get('/vendors', { params });
    setVendors(r.data);
  }, [tradeFilter, search]);
  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    try {
      if (editVendor) { await api.put(`/vendors/${editVendor.id}`, form); toast.success('Vendor updated'); }
      else { await api.post('/vendors', form); toast.success('Vendor added'); }
      setDialogOpen(false); setEditVendor(null); setForm(EMPTY); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    await api.delete(`/vendors/${id}`); toast.success('Deleted'); load();
  };

  const stars = (n) => Array.from({ length: 5 }, (_, i) => (
    <Star key={i} className={`w-3 h-3 ${i < n ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
  ));

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="vendors-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Vendors & Subs</h1>
          <p className="text-slate-500 text-sm mt-1">{vendors.length} vendors</p>
        </div>
        <Button className="bg-sky-500 hover:bg-sky-600 text-white" onClick={() => { setEditVendor(null); setForm(EMPTY); setDialogOpen(true); }}
          data-testid="add-vendor-btn"><Plus className="w-4 h-4 mr-1" /> Add Vendor</Button>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search vendors..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-10" data-testid="vendor-search" />
        </div>
        <Select value={tradeFilter} onValueChange={setTradeFilter}>
          <SelectTrigger className="w-44 h-10"><SelectValue placeholder="All Trades" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Trades</SelectItem>
            {TRADES.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {vendors.map(v => (
          <Card key={v.id} className={`border-slate-100 hover:shadow-md transition-shadow ${!v.is_active ? 'opacity-50' : ''}`} data-testid={`vendor-card-${v.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-slate-800">{v.name}</p>
                  <Badge className={`text-[10px] mt-1 capitalize ${TRADE_COLORS[v.trade] || 'bg-slate-100 text-slate-600'}`}>{v.trade}</Badge>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditVendor(v); setForm(v); setDialogOpen(true); }}><Pencil className="w-3.5 h-3.5 text-slate-400" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(v.id)}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                </div>
              </div>
              {v.contact_name && <p className="text-xs text-slate-600 mb-1">{v.contact_name}</p>}
              <div className="space-y-0.5 mb-2">
                {v.phone && <p className="text-[11px] text-slate-500 flex items-center gap-1"><Phone className="w-2.5 h-2.5" />{v.phone}</p>}
                {v.email && <p className="text-[11px] text-slate-500 flex items-center gap-1"><Mail className="w-2.5 h-2.5" />{v.email}</p>}
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-0.5">{stars(v.rating || 0)}</div>
                <div className="flex items-center gap-2">
                  {v.insurance_on_file ? (
                    <Badge className="bg-emerald-100 text-emerald-700 text-[9px]"><Shield className="w-2.5 h-2.5 mr-0.5" />Insured</Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-600 text-[9px]">No Insurance</Badge>
                  )}
                </div>
              </div>
              {v.notes && <p className="text-[10px] text-slate-400 mt-2 line-clamp-2">{v.notes}</p>}
            </CardContent>
          </Card>
        ))}
        {vendors.length === 0 && <p className="col-span-3 text-center py-12 text-slate-400">No vendors yet. Add your first one!</p>}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="vendor-dialog">
          <DialogHeader><DialogTitle>{editVendor ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Company Name *</Label><Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} data-testid="vendor-form-name" /></div>
              <div><Label>Trade/Specialty</Label>
                <Select value={form.trade} onValueChange={v => setForm(p => ({...p, trade: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TRADES.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Contact Name</Label><Input value={form.contact_name} onChange={e => setForm(p => ({...p, contact_name: e.target.value}))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} /></div>
            </div>
            <div><Label>Address</Label><Input value={form.address} onChange={e => setForm(p => ({...p, address: e.target.value}))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Website</Label><Input value={form.website} onChange={e => setForm(p => ({...p, website: e.target.value}))} /></div>
              <div><Label>License #</Label><Input value={form.license_number} onChange={e => setForm(p => ({...p, license_number: e.target.value}))} /></div>
            </div>
            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="flex items-center gap-3 mb-2">
                <Switch checked={form.insurance_on_file} onCheckedChange={v => setForm(p => ({...p, insurance_on_file: v}))} data-testid="vendor-insurance-toggle" />
                <Label className="text-emerald-800 font-semibold">Insurance on File</Label>
              </div>
              {form.insurance_on_file && (
                <div><Label className="text-xs">Expiry Date</Label><Input type="date" value={form.insurance_expiry} onChange={e => setForm(p => ({...p, insurance_expiry: e.target.value}))} /></div>
              )}
            </div>
            <div>
              <Label>Rating (0-5)</Label>
              <div className="flex gap-1 mt-1">
                {[1,2,3,4,5].map(n => (
                  <button key={n} onClick={() => setForm(p => ({...p, rating: p.rating === n ? 0 : n}))} className="p-1" data-testid={`vendor-star-${n}`}>
                    <Star className={`w-5 h-5 ${n <= (form.rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                  </button>
                ))}
              </div>
            </div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={3} placeholder="Work history, reliability notes..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-sky-500 hover:bg-sky-600 text-white" onClick={handleSave} disabled={!form.name?.trim()} data-testid="vendor-form-save">{editVendor ? 'Update' : 'Add Vendor'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
